import React, { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, X, ZoomIn } from 'lucide-react';
import Sectionbreak from './Sectionbreak';

// Interior
import int1 from '../assets/images/services/interior/interior-design.jpg';
import int2 from '../assets/images/services/interior/interior-design1.jpg';
import int3 from '../assets/images/services/interior/interior-design6.jpg';
import int4 from '../assets/images/services/interior/interior-design8.jpg';
import int5 from '../assets/images/services/interior/interior-design10.jpg';
import int6 from '../assets/images/services/interior/interior-design12.jpg';
import int7 from '../assets/images/services/interior/interior-design13.jpg';

// Architecture / Construction
import arch1 from '../assets/images/edges-design2.jpg';
import arch2 from '../assets/images/edges-design3.jpg';
import arch3 from '../assets/images/edges-design4.jpg';
import arch4 from '../assets/images/edges-design5.jpg';
import arch5 from '../assets/images/edges-design7.jpg';
import arch6 from '../assets/images/edges-design9.jpg';
import arch7 from '../assets/images/edges-design11.jpg';
import arch8 from '../assets/images/services/architectural-design.jpg';
import arch9 from '../assets/images/services/Constuction.jpg';

// Projects
import proj1 from '../assets/images/projects/birembo/1.jpg';
import proj2 from '../assets/images/projects/birembo/2.jpg';
import proj3 from '../assets/images/projects/birembo/3.jpg';
import proj4 from '../assets/images/projects/birembo/4.jpg';
import proj5 from '../assets/images/projects/kanombe/Kanombe1.jpg';
import proj6 from '../assets/images/projects/kanombe/Kanombe2.jpg';
import proj7 from '../assets/images/projects/kanombe/Kanombe3.jpg';
import proj8 from '../assets/images/projects/kanombe/Kanombe4.jpg';
import proj9 from '../assets/images/projects/muya-apartment/1.jpg';
import proj10 from '../assets/images/projects/muya-apartment/2.jpg';
import proj11 from '../assets/images/projects/muya-apartment/3.jpg';
import proj12 from '../assets/images/projects/muya-apartment/4.jpg';

const categories = {
  All: [],
  'Interior Design': [int1, int2, int3, int4, int5, int6, int7],
  Architecture: [arch1, arch2, arch3, arch4, arch5, arch6, arch7, arch8, arch9],
  Projects: [proj1, proj2, proj3, proj4, proj5, proj6, proj7, proj8, proj9, proj10, proj11, proj12],
};
categories.All = [
  ...categories['Interior Design'],
  ...categories.Architecture,
  ...categories.Projects,
];

const tabs = Object.keys(categories);

function Lightbox({ images, index, onClose, onPrev, onNext }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const handleKey = useCallback((e) => {
    if (e.key === 'Escape') onClose();
    if (e.key === 'ArrowLeft') onPrev();
    if (e.key === 'ArrowRight') onNext();
  }, [onClose, onPrev, onNext]);

  useEffect(() => {
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [handleKey]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm"
      onClick={onClose}
    >
      {/* Counter */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 text-white/70 text-sm font-medium tabular-nums">
        {index + 1} / {images.length}
      </div>

      {/* Close */}
      <button
        className="absolute top-4 right-4 text-white/70 hover:text-white p-2 rounded-full hover:bg-white/10 transition"
        onClick={onClose}
        aria-label="Close"
      >
        <X size={24} />
      </button>

      {/* Prev */}
      <button
        className="absolute left-3 md:left-6 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10 transition"
        onClick={(e) => { e.stopPropagation(); onPrev(); }}
        aria-label="Previous"
      >
        <ChevronLeft size={32} />
      </button>

      {/* Image */}
      <img
        src={images[index]}
        alt={`Gallery ${index + 1}`}
        className="max-h-[85vh] max-w-[90vw] object-contain rounded shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />

      {/* Next */}
      <button
        className="absolute right-3 md:right-6 text-white/70 hover:text-white p-3 rounded-full hover:bg-white/10 transition"
        onClick={(e) => { e.stopPropagation(); onNext(); }}
        aria-label="Next"
      >
        <ChevronRight size={32} />
      </button>
    </div>
  );
}

const Gallery = () => {
  const [activeTab, setActiveTab] = useState(tabs[0]);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const images = categories[activeTab];

  const openLightbox = (i) => setLightboxIndex(i);
  const closeLightbox = () => setLightboxIndex(null);
  const goPrev = () => setLightboxIndex((i) => (i - 1 + images.length) % images.length);
  const goNext = () => setLightboxIndex((i) => (i + 1) % images.length);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setLightboxIndex(null);
  };

  return (
    <section className="py-16 bg-gray-50">
      <Sectionbreak title="Our Work" highlight="Gallery" description="" />

      <div className="max-w-7xl mx-auto px-4 md:px-12">
        {/* Tabs */}
        <div className="flex justify-center mb-10 flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              className={`px-5 py-2 rounded-lg text-sm font-medium transition-all duration-150 ${
                activeTab === tab
                  ? 'bg-teal-600 text-white shadow-sm'
                  : 'bg-white text-gray-600 border border-gray-200 hover:border-teal-400 hover:text-teal-600'
              }`}
            >
              {tab}
              <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                activeTab === tab ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-500'
              }`}>
                {categories[tab].length}
              </span>
            </button>
          ))}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {images.map((src, i) => (
            <button
              key={i}
              className="group relative overflow-hidden rounded-lg aspect-square bg-gray-100 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
              onClick={() => openLightbox(i)}
              aria-label={`View image ${i + 1}`}
            >
              <img
                src={src}
                alt={`${activeTab} ${i + 1}`}
                className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200 flex items-center justify-center">
                <ZoomIn
                  size={28}
                  className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-200 drop-shadow"
                />
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          index={lightboxIndex}
          onClose={closeLightbox}
          onPrev={goPrev}
          onNext={goNext}
        />
      )}
    </section>
  );
};

export default Gallery;
